import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useEffect, useMemo, useState } from 'react';
import { PushPin, Trash, Lightbulb, CaretLeft, CaretRight } from 'phosphor-react';
import { useTranslation } from '../../i18n';
import { useInsights, Insight, InsightCategory } from '../../hooks/useInsights';
import { theme } from '../../styles/theme';

interface InsightsViewProps {
  userId?: string | number;
  apiUrl?: string;
}

const CATEGORY_COLORS: Record<InsightCategory, string> = {
  reflection: '#9B6DD7',
  action: '#52B788',
  learning: '#6B9BDB',
  pattern: '#E67700',
};

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-self: stretch;
  flex: 1;
  min-height: 0;
  width: 100%;
  margin: 0 auto;
  padding: 0 0 80px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  cursor: pointer;
  flex-shrink: 0;

  &:active { transform: scale(0.95); }
`;

const PageTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 1.2;
  color: var(--color-text);
  margin: 8px 0 16px;
`;

const DetailTitle = styled(PageTitle)`
  flex: 1;
  margin: 0;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// ─── Session (parent) card ──────────────────────────────────────────────────

const SessionCard = styled.div<{ $index: number }>`
  position: relative;
  width: 100%;
  background: var(--color-card-bg);
  border: 1px solid var(--color-border-subtle);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  cursor: pointer;
  opacity: 0;
  animation: ${fadeInUp} 0.4s ease-out forwards;
  animation-delay: ${({ $index }) => $index * 60}ms;
  transition: transform 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: var(--color-border);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SessionCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SessionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InsightCountBadge = styled.span`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 11px;
  font-weight: 600;
  color: var(--color-primary);
  background: color-mix(in srgb, var(--color-primary) 15%, transparent);
  padding: 3px 8px;
  border-radius: 12px;
`;

const SessionDate = styled.span`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 11px;
  color: var(--color-text-muted);
`;

const SessionTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SessionPreview = styled.p`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CategoryDots = styled.div`
  display: flex;
  gap: 4px;
`;

const SessionFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
`;

const CategoryDot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

// ─── Insight (detail) card ──────────────────────────────────────────────────

const InsightCard = styled.div<{ $index: number }>`
  background: var(--color-card-bg);
  border: 1px solid var(--color-border-subtle);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: 0;
  animation: ${fadeInUp} 0.4s ease-out forwards;
  animation-delay: ${({ $index }) => $index * 60}ms;
  position: relative;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CategoryBadge = styled.span<{ $category: InsightCategory }>`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 4px 8px;
  border-radius: 6px;
  color: ${({ $category }) => CATEGORY_COLORS[$category]};
  background: ${({ $category }) => `${CATEGORY_COLORS[$category]}22`};
`;

const CardActions = styled.div`
  display: flex;
  gap: 4px;
`;

const IconButton = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? 'var(--color-primary)' : 'var(--color-text-muted)')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: var(--color-bg-elevated);
    color: var(--color-text);
  }

  &:active { transform: scale(0.92); }
`;

const CardTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
  line-height: 1.3;
`;

const CardContent = styled.p`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  margin: 0;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
`;

const DateLabel = styled.span`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 11px;
  color: var(--color-text-muted);
`;

// ─── Empty / Loading ────────────────────────────────────────────────────────

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
  gap: 16px;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--color-bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
`;

const EmptyText = styled.p`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  max-width: 280px;
  margin: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 64px 0;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-border-subtle);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ChevronWrap = styled.div`
  color: var(--color-text-muted);
`;

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr: string, lang: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (lang === 'tr') {
    if (diffDays === 0) return 'Bugün';
    if (diffDays === 1) return 'Dün';
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' });
  }
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long' });
}

function categoryLabel(category: InsightCategory, lang: string): string {
  const labels: Record<InsightCategory, { tr: string; en: string }> = {
    reflection: { tr: 'Farkındalık', en: 'Reflection' },
    action: { tr: 'Eylem', en: 'Action' },
    learning: { tr: 'Öğrenme', en: 'Learning' },
    pattern: { tr: 'Örüntü', en: 'Pattern' },
  };
  return labels[category][lang === 'tr' ? 'tr' : 'en'];
}

interface SessionGroup {
  sessionId: string | null;
  insights: Insight[];
  latestDate: string;
  title: string;
  preview: string;
  categories: InsightCategory[];
  isPinned: boolean;
}

function groupInsightsBySession(insights: Insight[]): SessionGroup[] {
  const groups = new Map<string, Insight[]>();
  for (const insight of insights) {
    const key = insight.session_id || 'no-session';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(insight);
  }

  const result: SessionGroup[] = [];
  for (const [key, groupInsights] of groups.entries()) {
    // Sort insights in this group: pinned first, then newest
    groupInsights.sort((a, b) => {
      const pa = a.is_pinned ? 1 : 0;
      const pb = b.is_pinned ? 1 : 0;
      if (pa !== pb) return pb - pa;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    const first = groupInsights[0];
    // Prefer the session-level title/summary produced by the LLM; fall back to first insight
    const title = first.session_title || first.title;
    const preview = first.session_summary || (
      groupInsights.length > 1
        ? groupInsights.slice(0, 3).map((i) => i.title).join(' · ')
        : first.content
    );
    const uniqueCategories = Array.from(new Set(groupInsights.map((i) => i.category)));
    const isPinned = groupInsights.some((i) => !!i.is_pinned);

    result.push({
      sessionId: key === 'no-session' ? null : key,
      insights: groupInsights,
      latestDate: first.created_at,
      title,
      preview: preview || first.content,
      categories: uniqueCategories,
      isPinned,
    });
  }

  // Sort groups: pinned first, then latest date DESC
  result.sort((a, b) => {
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
    return new Date(b.latestDate).getTime() - new Date(a.latestDate).getTime();
  });
  return result;
}

// ─── Component ──────────────────────────────────────────────────────────────

export const InsightsView: React.FC<InsightsViewProps> = ({ userId, apiUrl }) => {
  const { lang } = useTranslation();
  const { insights, isLoading, fetchInsights, togglePin, removeInsight } = useInsights({
    apiUrl,
    userId,
  });

  const [selectedSessionKey, setSelectedSessionKey] = useState<string | null>(null);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const sessionGroups = useMemo(() => groupInsightsBySession(insights), [insights]);

  const selectedGroup = useMemo(
    () => sessionGroups.find((g) => (g.sessionId || 'no-session') === selectedSessionKey) || null,
    [sessionGroups, selectedSessionKey]
  );

  // ─── Detail view: show insights for selected session ─────────────────────
  if (selectedGroup) {
    return (
      <Container>
        <HeaderRow>
          <BackButton onClick={() => setSelectedSessionKey(null)} aria-label="Back">
            <CaretLeft size={18} weight="bold" />
          </BackButton>
          <DetailTitle as="h2">{selectedGroup.title}</DetailTitle>
        </HeaderRow>

        <Grid>
          {selectedGroup.insights.map((insight, idx) => (
            <InsightCard key={insight.id} $index={idx}>
              <CardHeader>
                <CategoryBadge $category={insight.category}>
                  {categoryLabel(insight.category, lang)}
                </CategoryBadge>
                <CardActions>
                  <IconButton
                    onClick={() => removeInsight(insight.id)}
                    aria-label={lang === 'tr' ? 'Sil' : 'Delete'}
                  >
                    <Trash size={16} weight="regular" />
                  </IconButton>
                </CardActions>
              </CardHeader>
              <CardTitle>{insight.title}</CardTitle>
              <CardContent>{insight.content}</CardContent>
              <CardFooter>
                <DateLabel>{formatDate(insight.created_at, lang)}</DateLabel>
              </CardFooter>
            </InsightCard>
          ))}
        </Grid>
      </Container>
    );
  }

  // ─── Main view: session-grouped cards ─────────────────────────────────────
  return (
    <Container>
      <PageTitle>{lang === 'tr' ? 'İçgörülerim' : 'My Insights'}</PageTitle>

      {isLoading ? (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      ) : sessionGroups.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <Lightbulb size={28} weight="regular" />
          </EmptyIcon>
          <EmptyText>
            {lang === 'tr'
              ? 'Henüz içgörün yok. Bir konuşmadan sonra "+" menüsünden "İçgörü Al"a tıkla.'
              : 'No insights yet. After a conversation, tap "+" and choose "Get Insights".'}
          </EmptyText>
        </EmptyState>
      ) : (
        <Grid>
          {sessionGroups.map((group, idx) => (
            <SessionCard
              key={group.sessionId || 'no-session'}
              $index={idx}
              onClick={() => setSelectedSessionKey(group.sessionId || 'no-session')}
            >
              <SessionCardHeader>
                <SessionMeta>
                  <InsightCountBadge>
                    {group.insights.length} {lang === 'tr' ? 'içgörü' : group.insights.length === 1 ? 'insight' : 'insights'}
                  </InsightCountBadge>
                  <SessionDate>{formatDate(group.latestDate, lang)}</SessionDate>
                </SessionMeta>
                <CardActions onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    $active={group.isPinned}
                    onClick={async (e) => {
                      e.stopPropagation();
                      const nextState = !group.isPinned;
                      await Promise.all(
                        group.insights.map((i) =>
                          !!i.is_pinned !== nextState ? togglePin(i.id, !!i.is_pinned) : Promise.resolve()
                        )
                      );
                    }}
                    aria-label={lang === 'tr' ? 'Sabitle' : 'Pin'}
                  >
                    <PushPin size={16} weight={group.isPinned ? 'fill' : 'regular'} />
                  </IconButton>
                  <IconButton
                    onClick={async (e) => {
                      e.stopPropagation();
                      const confirmMsg = lang === 'tr'
                        ? `Bu konuşmadan çıkan ${group.insights.length} içgörüyü silmek istediğine emin misin?`
                        : `Delete all ${group.insights.length} insights from this conversation?`;
                      if (!window.confirm(confirmMsg)) return;
                      await Promise.all(group.insights.map((i) => removeInsight(i.id)));
                    }}
                    aria-label={lang === 'tr' ? 'Sil' : 'Delete'}
                  >
                    <Trash size={16} weight="regular" />
                  </IconButton>
                </CardActions>
              </SessionCardHeader>
              <SessionTitle>{group.title}</SessionTitle>
              <SessionPreview>{group.preview}</SessionPreview>
              <SessionFooter>
                <CategoryDots>
                  {group.categories.map((cat) => (
                    <CategoryDot key={cat} $color={CATEGORY_COLORS[cat]} />
                  ))}
                </CategoryDots>
                <ChevronWrap>
                  <CaretRight size={16} weight="regular" />
                </ChevronWrap>
              </SessionFooter>
            </SessionCard>
          ))}
        </Grid>
      )}
    </Container>
  );
};
